import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useState } from 'react';
import {
  partyDirectoryApi,
  type ProviderContactView,
  type ProviderQuote,
  type ProviderView,
} from './party-directory-api';
import { PrototypeIcon } from './PrototypeIcon';

/** La promotion du Fournisseur est une décision métier, sans création de compte ni invitation. */
export function ProviderDirectoryPanel({
  csrfToken,
  initialProvider = null,
}: {
  readonly csrfToken: string;
  readonly initialProvider?: ProviderView | null;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [screen, setScreen] = useState<'overview' | 'suppliers' | 'detail'>(
    initialProvider ? 'detail' : 'overview',
  );
  const [page, setPage] = useState(0);
  const [providerCount, setProviderCount] = useState(0);
  const [supplierCount, setSupplierCount] = useState(0);
  const [selected, setSelected] = useState<ProviderView | null>(initialProvider);
  const [providers, setProviders] = useState<readonly ProviderView[]>([]);
  const [candidates, setCandidates] = useState<readonly ProviderView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<ProviderView | null>(null);
  const [emailTarget, setEmailTarget] = useState<ProviderView | null>(null);
  const [providerEmail, setProviderEmail] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  // A replacement keeps the same private URL; changing the query forces every visible image to reload.
  const [logoRevision, setLogoRevision] = useState<Record<string, number>>({});
  const [photoRevision, setPhotoRevision] = useState<Record<string, number>>({});
  const [quotes, setQuotes] = useState<readonly ProviderQuote[]>([]);
  const [contacts, setContacts] = useState<readonly ProviderContactView[]>([]);
  const [contactEditor, setContactEditor] = useState<ProviderContactView | 'new' | null>(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactPosition, setContactPosition] = useState('');
  const [pendingContactInvite, setPendingContactInvite] = useState(false);
  const [quoteEditor, setQuoteEditor] = useState(false);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteFile, setQuoteFile] = useState<File | null>(null);
  const [demotionTarget, setDemotionTarget] = useState<ProviderView | null>(null);
  const [demotionConfirmed, setDemotionConfirmed] = useState(false);

  useEffect(() => {
    if (screen !== 'detail' || !selected) return;
    let active = true;
    void partyDirectoryApi.providerQuotes(selected.id).then(
      (items) => {
        if (active) setQuotes(items);
      },
      () => {
        if (active) setError('Devis prestataires indisponibles.');
      },
    );
    return () => {
      active = false;
    };
  }, [screen, selected?.id]);

  useEffect(() => {
    if (screen !== 'detail' || !selected) return;
    let active = true;
    void partyDirectoryApi.providerContacts(selected.id).then(
      (items) => {
        if (active) setContacts(items);
      },
      () => {
        if (active) setError('Contacts prestataires indisponibles.');
      },
    );
    return () => {
      active = false;
    };
  }, [screen, selected?.id]);

  const editContact = (contact: ProviderContactView | 'new'): void => {
    setContactEditor(contact);
    setContactName(contact === 'new' ? '' : contact.displayName);
    setContactEmail(contact === 'new' ? '' : (contact.email ?? ''));
    setContactPhone(contact === 'new' ? '' : (contact.phone ?? ''));
    setContactPosition(contact === 'new' ? '' : (contact.position ?? ''));
    setError(null);
    setNotice(null);
  };

  const saveContact = async (provider: ProviderView): Promise<void> => {
    setBusy(true);
    try {
      await partyDirectoryApi.saveProviderContact(
        provider.id,
        contactEditor === 'new' ? null : (contactEditor?.id ?? null),
        {
          displayName: contactName,
          email: contactEmail || null,
          phone: contactPhone || null,
          position: contactPosition || null,
        },
        csrfToken,
      );
      setContacts(await partyDirectoryApi.providerContacts(provider.id));
      setContactEditor(null);
      setError(null);
      setNotice('Contact enregistré.');
    } catch {
      setError('Contact non enregistré. Vérifiez les coordonnées.');
    } finally {
      setBusy(false);
    }
  };

  const uploadContactPhoto = async (
    provider: ProviderView,
    contact: ProviderContactView,
    file: File,
  ): Promise<void> => {
    setBusy(true);
    try {
      await partyDirectoryApi.saveProviderContactPhoto(provider.id, contact.id, file, csrfToken);
      setContacts(await partyDirectoryApi.providerContacts(provider.id));
      setPhotoRevision((current) => ({ ...current, [contact.id]: (current[contact.id] ?? 0) + 1 }));
      setError(null);
      setNotice('Photo enregistrée.');
    } catch {
      setError('Photo non enregistrée. Choisissez un PNG, JPEG ou WebP de 512 Ko maximum.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let active = true;
    const timeout = window.setTimeout(
      () => {
        void partyDirectoryApi
          .providers(
            screen === 'detail' ? '' : query,
            page,
            screen === 'suppliers' ? 'suppliers' : 'providers',
          )
          .then(
            (view) => {
              if (!active) return;
              setProviders(view.providers);
              setCandidates(view.candidates);
              setProviderCount(view.providerCount);
              setSupplierCount(view.supplierCount);
            },
            () => {
              if (active) setError('Liste des prestataires indisponible.');
            },
          );
      },
      query ? 200 : 0,
    );
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [query, page, screen]);

  const promote = async (candidate: ProviderView): Promise<void> => {
    if (!window.confirm(`Confirmer que « ${candidate.displayName} » est bien votre prestataire ?`))
      return;
    setBusy(true);
    try {
      const promoted = await partyDirectoryApi.promoteProvider(
        candidate.supplierReferenceId,
        csrfToken,
      );
      const view = await partyDirectoryApi.providers('', 0);
      setProviders(view.providers);
      setCandidates(view.candidates);
      setProviderCount(view.providerCount);
      setSupplierCount(view.supplierCount);
      setSelected(promoted);
      setScreen('detail');
      setError(null);
    } catch {
      setError('Promotion impossible. Vérifiez la fiche et actualisez la page.');
    } finally {
      setBusy(false);
    }
  };

  const demote = async (provider: ProviderView): Promise<void> => {
    if (!demotionConfirmed) return;
    setBusy(true);
    try {
      await partyDirectoryApi.demoteProvider(provider.id, provider.version, csrfToken);
      const view = await partyDirectoryApi.providers('', 0);
      setProviders(view.providers);
      setCandidates(view.candidates);
      setProviderCount(view.providerCount);
      setSupplierCount(view.supplierCount);
      setDemotionTarget(null);
      setSelected(null);
      setScreen('overview');
      setError(null);
      setNotice('La fiche est repassée dans les fournisseurs. Ses informations sont conservées.');
    } catch {
      setError('Qualification inchangée. Actualisez la fiche et réessayez.');
      setDemotionTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (provider: ProviderView, file: File): Promise<void> => {
    setBusy(true);
    try {
      await partyDirectoryApi.saveProviderLogo(provider.id, file, csrfToken);
      setLogoRevision((current) => ({
        ...current,
        [provider.id]: (current[provider.id] ?? 0) + 1,
      }));
      const updated = { ...provider, hasLogo: true };
      setSelected(updated);
      setProviders((current) => current.map((item) => (item.id === provider.id ? updated : item)));
      setError(null);
      setNotice('Logo enregistré.');
    } catch {
      setError('Logo non enregistré. Choisissez un PNG, JPEG ou WebP de 512 Ko maximum.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="client-directory provider-directory">
      {notice && !(screen === 'detail' && notice === 'Logo enregistré.') && (
        <p role="status" className="client-directory__success tosho-feedback is-success">
          {notice}
        </p>
      )}
      {screen !== 'overview' && (
        <button
          type="button"
          className="client-directory__back"
          onClick={() => {
            setScreen('overview');
            setQuery('');
            setPage(0);
            setSelected(null);
            setEmailTarget(null);
            setInviteTarget(null);
            setNotice(null);
            setError(null);
          }}
        >
          <PrototypeIcon name="arrow-left" /> Retour aux prestataires
        </button>
      )}
      <header className="client-directory__heading">
        <div>
          <p className="client-directory__eyebrow">Répertoire central</p>
          <h1>
            {screen === 'suppliers'
              ? 'Fournisseurs Freebe'
              : screen === 'detail'
                ? selected?.displayName
                : 'Prestataires'}
          </h1>
          <p>
            {screen === 'suppliers'
              ? 'Choisissez les fournisseurs qui réalisent une prestation.'
              : screen === 'detail'
                ? 'Relation confirmée · référence fournisseur Freebe'
                : 'Retrouvez les prestataires confirmés et leurs coordonnées.'}
          </p>
        </div>
        {screen === 'overview' && (
          <button
            type="button"
            className="client-directory__primary"
            onClick={() => {
              setScreen('suppliers');
              setPage(0);
            }}
          >
            ＋ Ajouter un prestataire
          </button>
        )}
      </header>
      {error && !quoteEditor && !contactEditor && !inviteTarget && !emailTarget && (
        <p
          role="alert"
          className="client-directory__error client-directory__alert tosho-feedback is-error"
        >
          {error}
        </p>
      )}
      {screen === 'overview' && (
        <>
          <div className="client-directory__overview-cards provider-directory__cards">
            <button
              type="button"
              className="client-directory__metric"
              onClick={() =>
                document.getElementById('provider-list')?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              <PrototypeIcon name="briefcase-business" />
              <strong>{providerCount}</strong>
              <span>Prestataires</span>
            </button>
            <div className="client-directory__import-note">
              <PrototypeIcon name="cloud-upload" />
              <span>
                <strong>Fournisseurs consolidés</strong>
                <small>{supplierCount} fournisseurs à qualifier</small>
              </span>
              <button
                type="button"
                onClick={() => {
                  setScreen('suppliers');
                  setPage(0);
                }}
              >
                <PrototypeIcon name="search" /> Choisir un fournisseur
              </button>
            </div>
          </div>
          <section
            id="provider-list"
            className="client-directory__table-card provider-directory__surface"
          >
            <div className="client-directory__table-toolbar">
              <h2>Prestataires</h2>
              <div className="client-directory__search" role="search">
                <PrototypeIcon name="search" />
                <input
                  type="search"
                  aria-label="Rechercher un prestataire"
                  value={query}
                  placeholder="Rechercher un prestataire"
                  onChange={(event) => setQuery(event.currentTarget.value)}
                />
              </div>
            </div>
            <ProviderCards
              entries={providers}
              logoRevision={logoRevision}
              onOpen={(item) => {
                setNotice(null);
                setSelected(item);
                setScreen('detail');
              }}
            />
          </section>
        </>
      )}
      {screen === 'suppliers' && (
        <section className="client-directory__table-card">
          <div className="client-directory__table-toolbar">
            <div className="client-directory__search" role="search">
              <PrototypeIcon name="search" />
              <input
                type="search"
                aria-label="Rechercher un fournisseur Freebe"
                value={query}
                maxLength={120}
                placeholder="Rechercher un fournisseur Freebe"
                onChange={(event) => {
                  setQuery(event.currentTarget.value);
                  setPage(0);
                }}
              />
            </div>
            <span>{supplierCount} résultat(s)</span>
          </div>
          <div className="client-directory__table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fournisseur</th>
                  <th>E-mail</th>
                  <th>Qualification</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => (
                  <tr key={candidate.id}>
                    <td>
                      <strong>{candidate.displayName}</strong>
                    </td>
                    <td>{candidate.email ?? 'À renseigner'}</td>
                    <td>
                      <button
                        className="provider-directory__qualify"
                        type="button"
                        disabled={busy}
                        onClick={() => void promote(candidate)}
                      >
                        <PrototypeIcon name="plus" /> Confirmer comme prestataire
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {candidates.length === 0 && (
            <p className="client-directory__empty">Aucun résultat correspondant.</p>
          )}
          <div className="client-directory__pagination">
            <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Précédent
            </button>
            <span>Page {page + 1}</span>
            <button
              type="button"
              disabled={(page + 1) * 50 >= supplierCount}
              onClick={() => setPage(page + 1)}
            >
              Suivant
            </button>
          </div>
        </section>
      )}
      {screen === 'detail' && selected && (
        <section className="client-directory__table-card provider-directory__detail">
          <p className="client-directory__eyebrow">Fiche prestataire</p>
          <div className="provider-directory__identity">
            <span className="provider-directory__avatar">
              {selected.hasLogo ? (
                <img
                  src={`/api/directory/providers/${encodeURIComponent(selected.id)}/logo?v=${logoRevision[selected.id] ?? 0}`}
                  alt=""
                />
              ) : (
                selected.displayName.slice(0, 2).toUpperCase()
              )}
            </span>
            <h2>{selected.displayName}</h2>
          </div>
          <p>{selected.email ?? 'E-mail à confirmer avant une invitation'}</p>
          <label className="provider-directory__upload">
            {selected.hasLogo ? 'Remplacer le logo' : 'Ajouter le logo de la société'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={busy}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) void uploadLogo(selected, file);
                event.currentTarget.value = '';
              }}
            />
          </label>
          {notice === 'Logo enregistré.' && (
            <p role="status" className="client-directory__success tosho-feedback is-success">
              {notice}
            </p>
          )}
          <div className="client-directory__actions">
            <button
              type="button"
              onClick={() => {
                setEmailTarget(selected);
                setProviderEmail(selected.email ?? '');
                setNotice(null);
                setPendingContactInvite(false);
              }}
            >
              <PrototypeIcon name="settings" /> Confirmer l’e-mail
            </button>
            {selected.email && (
              <button
                type="button"
                onClick={() => {
                  setInviteTarget(selected);
                  setNotice(null);
                }}
              >
                <PrototypeIcon name="plus" /> Préparer une invitation
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setDemotionConfirmed(false);
                setDemotionTarget(selected);
                setError(null);
              }}
            >
              Repasser en fournisseur
            </button>
          </div>
        </section>
      )}
      {demotionTarget && (
        <ModalBackdrop
          className="client-directory__overlay client-directory provider-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDemotionTarget(null);
          }}
          aria-label="Repasser en fournisseur"
          close={<ModalCloseButton aria-label="Fermer" onClick={() => setDemotionTarget(null)} />}
        >
          <section className="tosho-modal-panel client-directory__modal client-directory__requalify-dialog">
            <header className="client-directory__modal-heading tosho-modal-header">
              <h2>Repasser en fournisseur</h2>
            </header>
            <p>
              « {demotionTarget.displayName} » quittera la liste des prestataires. La référence
              Freebe, les contacts et les devis resteront conservés.
            </p>
            <label className="client-directory__confirmation">
              <input
                type="checkbox"
                checked={demotionConfirmed}
                onChange={(event) => setDemotionConfirmed(event.currentTarget.checked)}
              />
              Je confirme cette correction.
            </label>
            <div className="client-directory__modal-footer">
              <button type="button" onClick={() => setDemotionTarget(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="client-directory__primary"
                disabled={!demotionConfirmed || busy}
                onClick={() => void demote(demotionTarget)}
              >
                Confirmer
              </button>
            </div>
          </section>
        </ModalBackdrop>
      )}
      {screen === 'detail' && selected && (
        <section className="client-directory__table-card provider-directory__contacts">
          <div className="client-directory__table-toolbar">
            <h2>Contacts du prestataire</h2>
            <button
              type="button"
              className="provider-directory__qualify"
              onClick={() => editContact('new')}
            >
              <PrototypeIcon name="plus" /> Ajouter un contact
            </button>
          </div>
          <div className="provider-directory__card-grid">
            {contacts.map((contact) => (
              <article key={contact.id} className="provider-directory__contact-card">
                <button
                  className="client-directory__contact-edit"
                  type="button"
                  onClick={() => editContact(contact)}
                  aria-label={`Modifier ${contact.displayName}`}
                  title={`Modifier ${contact.displayName}`}
                >
                  <PrototypeIcon name="pencil" />
                </button>
                <div className="client-directory__contact-identity">
                  <label className="client-directory__contact-portrait client-directory__contact-portrait--editable">
                    <span className="client-directory__contact-avatar" aria-hidden="true">
                      {contact.hasPhoto ? (
                        <img
                          src={`/api/directory/providers/${encodeURIComponent(selected.id)}/contacts/${encodeURIComponent(contact.id)}/photo?v=${photoRevision[contact.id] ?? 0}`}
                          alt=""
                        />
                      ) : (
                        contact.displayName
                          .split(/\s+/)
                          .slice(0, 2)
                          .map((part) => part[0])
                          .join('')
                          .toLocaleUpperCase('fr-FR')
                      )}
                    </span>
                    <span
                      className="client-directory__contact-company"
                      aria-label={`Logo de ${selected.displayName}`}
                    >
                      {selected.hasLogo ? (
                        <img
                          src={`/api/directory/providers/${encodeURIComponent(selected.id)}/logo?v=${logoRevision[selected.id] ?? 0}`}
                          alt=""
                        />
                      ) : (
                        selected.displayName.slice(0, 2).toLocaleUpperCase('fr-FR')
                      )}
                    </span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      aria-label={`Ajouter ou changer la photo de ${contact.displayName}`}
                      disabled={busy}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        if (file) void uploadContactPhoto(selected, contact, file);
                        event.currentTarget.value = '';
                      }}
                    />
                  </label>
                  <strong>{contact.displayName}</strong>
                  <span className="client-directory__contact-position">
                    {contact.position || 'Rôle à préciser'}
                  </span>
                  {contact.email ? (
                    <a className="client-directory__contact-email" href={`mailto:${contact.email}`}>
                      {contact.email}
                    </a>
                  ) : (
                    <span className="client-directory__contact-email">E-mail à confirmer</span>
                  )}
                  <span className="client-directory__contact-phone">
                    {contact.phone || 'Téléphone à préciser'}
                  </span>
                </div>
                {contact.email && (
                  <button
                    type="button"
                    className="client-directory__contact-invite"
                    onClick={() => {
                      if (contact.email !== selected.email) {
                        setEmailTarget(selected);
                        setProviderEmail(contact.email ?? '');
                        setPendingContactInvite(true);
                      } else {
                        setInviteTarget(selected);
                        setPendingContactInvite(false);
                      }
                      const names = contact.displayName.trim().split(/\s+/);
                      setGivenName(names[0] ?? '');
                      setFamilyName(names.slice(1).join(' '));
                      setError(null);
                      setNotice(null);
                    }}
                  >
                    <PrototypeIcon name="send" /> Inviter
                  </button>
                )}
              </article>
            ))}
            {contacts.length === 0 && <p>Aucun contact rattaché.</p>}
          </div>
        </section>
      )}
      {screen === 'detail' && selected && contactEditor && (
        <ModalBackdrop
          className="client-directory__overlay client-directory provider-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setContactEditor(null);
          }}
          aria-label={
            contactEditor === 'new'
              ? 'Ajouter un contact prestataire'
              : 'Modifier le contact prestataire'
          }
          close={<ModalCloseButton onClick={() => setContactEditor(null)} />}
        >
          <form
            className="tosho-modal-panel client-directory__modal provider-directory__dialog"
            onSubmit={(event) => {
              event.preventDefault();
              void saveContact(selected);
            }}
          >
            <header className="tosho-modal-heading tosho-modal-header">
              <h2>
                <PrototypeIcon name={contactEditor === 'new' ? 'plus' : 'settings'} />{' '}
                {contactEditor === 'new' ? 'Ajouter un contact' : 'Modifier le contact'}
              </h2>
            </header>
            {error && (
              <p role="alert" className="client-directory__error tosho-feedback is-error">
                {error}
              </p>
            )}
            <label>
              Nom
              <input
                required
                maxLength={255}
                value={contactName}
                onChange={(event) => setContactName(event.currentTarget.value)}
              />
            </label>
            <label>
              E-mail
              <input
                type="email"
                maxLength={320}
                value={contactEmail}
                onChange={(event) => setContactEmail(event.currentTarget.value)}
              />
            </label>
            <label>
              Téléphone
              <input
                maxLength={32}
                value={contactPhone}
                onChange={(event) => setContactPhone(event.currentTarget.value)}
              />
            </label>
            <label>
              Fonction
              <input
                maxLength={255}
                value={contactPosition}
                onChange={(event) => setContactPosition(event.currentTarget.value)}
              />
            </label>
            <div className="client-directory__actions">
              <button type="submit" className="client-directory__primary" disabled={busy}>
                Enregistrer
              </button>
              <button type="button" onClick={() => setContactEditor(null)}>
                Annuler
              </button>
            </div>
          </form>
        </ModalBackdrop>
      )}
      {screen === 'detail' && selected && (
        <section className="provider-directory__access client-directory__table-card">
          <p className="client-directory__eyebrow">Accès</p>
          <h2>Matrice des droits</h2>
          <p>
            Les accès aux projets, SAV, FAQ et documents seront attribués par espace après
            invitation.
          </p>
          <div className="provider-directory__rights">
            <span>Projets</span>
            <span>SAV</span>
            <span>FAQ</span>
            <span>Documents</span>
          </div>
        </section>
      )}
      {screen === 'detail' && selected && (
        <section className="client-directory__table-card provider-directory__detail">
          <p className="client-directory__eyebrow">Documents privés</p>
          <h2>Devis prestataires</h2>
          <button type="button" onClick={() => setQuoteEditor(true)}>
            <PrototypeIcon name="plus" /> Ajouter un devis PDF
          </button>
          {quotes.length === 0 && <p>Aucun devis enregistré.</p>}
          <ul className="provider-directory__quotes">
            {quotes.map((quote) => (
              <li key={quote.id}>
                <a
                  href={`/api/directory/providers/${encodeURIComponent(selected.id)}/quotes/${encodeURIComponent(quote.id)}/file`}
                >
                  {quote.title}
                </a>
                <small>
                  {quote.amount ? `${quote.amount} € · ` : ''}
                  {new Date(quote.createdAt).toLocaleDateString('fr-FR')}
                </small>
              </li>
            ))}
          </ul>
        </section>
      )}
      {screen === 'detail' && selected && quoteEditor && (
        <ModalBackdrop
          className="client-directory__overlay client-directory provider-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setQuoteEditor(false);
          }}
          aria-label="Ajouter un devis prestataire"
          close={<ModalCloseButton onClick={() => setQuoteEditor(false)} />}
        >
          <form
            className="tosho-modal-panel client-directory__modal provider-directory__dialog"
            onSubmit={(event) => {
              event.preventDefault();
              if (!quoteFile) return;
              setBusy(true);
              void partyDirectoryApi
                .addProviderQuote(
                  selected.id,
                  quoteTitle,
                  quoteAmount || null,
                  quoteFile,
                  csrfToken,
                )
                .then(
                  async () => {
                    setQuotes(await partyDirectoryApi.providerQuotes(selected.id));
                    setQuoteEditor(false);
                    setQuoteTitle('');
                    setQuoteAmount('');
                    setQuoteFile(null);
                    setError(null);
                    setNotice('Devis enregistré.');
                  },
                  () =>
                    setError('Devis non enregistré. Vérifiez le PDF (2 Mo maximum) et les champs.'),
                )
                .finally(() => setBusy(false));
            }}
          >
            <header className="tosho-modal-heading tosho-modal-header">
              <h2>Ajouter un devis de {selected.displayName}</h2>
            </header>
            {error && (
              <p
                role="alert"
                className="client-directory__error client-directory__alert tosho-feedback is-error"
              >
                {error}
              </p>
            )}
            <label>
              Titre
              <input
                required
                maxLength={255}
                value={quoteTitle}
                onChange={(event) => setQuoteTitle(event.currentTarget.value)}
              />
            </label>
            <label>
              Montant en euros (facultatif)
              <input
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]{1,2})?"
                value={quoteAmount}
                onChange={(event) => setQuoteAmount(event.currentTarget.value)}
              />
            </label>
            <label>
              PDF privé (2 Mo maximum)
              <input
                required
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => setQuoteFile(event.currentTarget.files?.[0] ?? null)}
              />
            </label>
            <div className="client-directory__actions">
              <button
                className="client-directory__primary"
                type="submit"
                disabled={busy || !quoteFile}
              >
                Enregistrer le devis
              </button>
              <button type="button" onClick={() => setQuoteEditor(false)}>
                Annuler
              </button>
            </div>
          </form>
        </ModalBackdrop>
      )}
      {screen === 'detail' && emailTarget && (
        <ModalBackdrop
          className="client-directory__overlay client-directory provider-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setEmailTarget(null);
          }}
          aria-label="Confirmer l’e-mail Prestataire"
          close={<ModalCloseButton onClick={() => setEmailTarget(null)} />}
        >
          <section className="tosho-modal-panel client-directory__modal provider-directory__dialog">
            <header className="tosho-modal-heading tosho-modal-header">
              <h2>Adresse de {emailTarget.displayName}</h2>
            </header>
            {error && (
              <p
                role="alert"
                className="client-directory__error client-directory__alert tosho-feedback is-error"
              >
                {error}
              </p>
            )}
            <p>
              Vérifiez l’adresse auprès du Prestataire avant de confirmer. La source Freebe reste
              inchangée.
            </p>
            <form
              className="client-directory__fields"
              onSubmit={(event) => {
                event.preventDefault();
                setBusy(true);
                void partyDirectoryApi
                  .confirmProviderEmail(
                    emailTarget.id,
                    emailTarget.version,
                    providerEmail,
                    csrfToken,
                  )
                  .then(
                    (updated) => {
                      setProviders((current) =>
                        current.map((entry) => (entry.id === updated.id ? updated : entry)),
                      );
                      setSelected(updated);
                      setEmailTarget(null);
                      setInviteTarget(pendingContactInvite ? updated : null);
                      setPendingContactInvite(false);
                      setError(null);
                      setNotice('Adresse confirmée. Vous pouvez maintenant préparer l’invitation.');
                    },
                    () =>
                      setError(
                        'Adresse non enregistrée. Actualisez la liste si la fiche a changé ou si un compte y est déjà rattaché.',
                      ),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              <label>
                E-mail{' '}
                <input
                  type="email"
                  value={providerEmail}
                  maxLength={320}
                  required
                  onChange={(event) => setProviderEmail(event.currentTarget.value)}
                />
              </label>
              <div className="client-directory__actions">
                <button type="submit" className="client-directory__primary" disabled={busy}>
                  Confirmer l’adresse
                </button>
                <button type="button" onClick={() => setEmailTarget(null)}>
                  Annuler
                </button>
              </div>
            </form>
          </section>
        </ModalBackdrop>
      )}
      {screen === 'detail' && inviteTarget?.email && (
        <ModalBackdrop
          className="client-directory__overlay client-directory provider-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setInviteTarget(null);
          }}
          aria-label="Inviter le Prestataire"
          close={<ModalCloseButton onClick={() => setInviteTarget(null)} />}
        >
          <section className="tosho-modal-panel client-directory__modal provider-directory__dialog">
            <header className="tosho-modal-heading tosho-modal-header">
              <h2>Inviter {inviteTarget.displayName}</h2>
            </header>
            {error && (
              <p
                role="alert"
                className="client-directory__error client-directory__alert tosho-feedback is-error"
              >
                {error}
              </p>
            )}
            <p>
              E-mail confirmé : {inviteTarget.email}. Aucun Projet ou document ne sera attribué par
              cette invitation.
            </p>
            <form
              className="client-directory__fields"
              onSubmit={(event) => {
                event.preventDefault();
                if (!inviteTarget.email) return;
                setBusy(true);
                void partyDirectoryApi
                  .inviteProvider(
                    inviteTarget.id,
                    inviteTarget.email,
                    givenName,
                    familyName,
                    csrfToken,
                  )
                  .then(
                    (result) => {
                      setNotice(
                        result.status === 'ACTIVE'
                          ? 'Compte déjà actif et rattaché.'
                          : 'Invitation envoyée.',
                      );
                      setInviteTarget(null);
                      setError(null);
                    },
                    () =>
                      setError(
                        'Invitation impossible. Vérifiez l’identité et l’adresse, puis réessayez.',
                      ),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              <label>
                Prénom{' '}
                <input
                  value={givenName}
                  maxLength={100}
                  required
                  onChange={(event) => setGivenName(event.currentTarget.value)}
                />
              </label>
              <label>
                Nom{' '}
                <input
                  value={familyName}
                  maxLength={100}
                  required
                  onChange={(event) => setFamilyName(event.currentTarget.value)}
                />
              </label>
              <div className="client-directory__actions">
                <button type="submit" className="client-directory__primary" disabled={busy}>
                  Envoyer l’invitation
                </button>
                <button type="button" onClick={() => setInviteTarget(null)}>
                  Annuler
                </button>
              </div>
            </form>
          </section>
        </ModalBackdrop>
      )}
    </div>
  );
}

function ProviderCards({
  entries,
  onOpen,
  logoRevision,
}: {
  entries: readonly ProviderView[];
  onOpen: (entry: ProviderView) => void;
  logoRevision: Readonly<Record<string, number>>;
}): React.JSX.Element {
  return (
    <div className="provider-directory__card-grid">
      {entries.map((entry) => (
        <button
          className="provider-directory__card"
          key={entry.id}
          type="button"
          onClick={() => onOpen(entry)}
        >
          <span className="provider-directory__avatar">
            {entry.hasLogo ? (
              <img
                src={`/api/directory/providers/${encodeURIComponent(entry.id)}/logo?v=${logoRevision[entry.id] ?? 0}`}
                alt=""
              />
            ) : (
              entry.displayName.slice(0, 2).toLocaleUpperCase('fr-FR')
            )}
          </span>
          <span>
            <strong>{entry.displayName}</strong>
            <small>{entry.email ?? 'E-mail à confirmer'}</small>
          </span>
          <span className="provider-directory__arrow">›</span>
        </button>
      ))}
    </div>
  );
}
